import React, { useMemo } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader } from './ui/card';
import { newInvestissementLine, parseNum } from './devisQuoteState';

const fmtEur = (n) =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(
    Number.isFinite(n) ? n : 0
  );

const DevisQuoteInvestissementTab = ({
  investissement,
  operationCategories,
  theme,
  patchInvestissement,
  canEdit,
}) => {
  const inputStyle = {
    borderColor: theme.border,
    backgroundColor: theme.inputBackground,
    color: theme.text,
  };

  const linesByCategory = investissement?.linesByCategory || {};
  const anneesStr = investissement?.annees_amortissement ?? '3';

  const { totalClientHt, totalPrestataireHt } = useMemo(() => {
    let tc = 0;
    let tp = 0;
    for (const lines of Object.values(linesByCategory)) {
      if (!Array.isArray(lines)) continue;
      for (const l of lines) {
        tc += parseNum(l.client_ht, 0);
        tp += parseNum(l.prestataire_ht, 0);
      }
    }
    return { totalClientHt: tc, totalPrestataireHt: tp };
  }, [linesByCategory]);

  const categoryInvestTotals = useMemo(() => {
    const out = {};
    for (const cat of operationCategories) {
      const lines = linesByCategory[cat.id] || [];
      let c = 0;
      let p = 0;
      for (const l of lines) {
        c += parseNum(l.client_ht, 0);
        p += parseNum(l.prestataire_ht, 0);
      }
      out[cat.id] = { clientHt: c, prestataireHt: p };
    }
    return out;
  }, [linesByCategory, operationCategories]);

  const nAnnees = parseNum(anneesStr, 0);
  const showAmortAnnuel = nAnnees > 0;
  const amortAnnuelHt = showAmortAnnuel ? totalPrestataireHt / nAnnees : 0;

  const setAnnees = (raw) => {
    patchInvestissement((prev) => ({ ...prev, annees_amortissement: raw }));
  };

  const addLine = (catId) => {
    patchInvestissement((prev) => ({
      ...prev,
      linesByCategory: {
        ...prev.linesByCategory,
        [catId]: [...(prev.linesByCategory[catId] || []), newInvestissementLine()],
      },
    }));
  };

  const removeLine = (catId, lineId) => {
    patchInvestissement((prev) => {
      const cur = prev.linesByCategory[catId] || [];
      return {
        ...prev,
        linesByCategory: {
          ...prev.linesByCategory,
          [catId]: cur.filter((l) => l.id !== lineId),
        },
      };
    });
  };

  const updateLine = (catId, lineId, patch) => {
    patchInvestissement((prev) => {
      const cur = prev.linesByCategory[catId] || [];
      return {
        ...prev,
        linesByCategory: {
          ...prev.linesByCategory,
          [catId]: cur.map((l) => (l.id === lineId ? { ...l, ...patch } : l)),
        },
      };
    });
  };

  if (!operationCategories.length) {
    return (
      <p
        className="text-sm leading-relaxed rounded-xl border-2 px-4 py-4"
        style={{ borderColor: theme.border, backgroundColor: theme.panelInset, color: theme.textSecondary }}
      >
        Aucune catégorie d’opération. Définissez-en dans{' '}
        <span className="font-semibold" style={{ color: theme.text }}>
          Paramètres → Opérations
        </span>
        .
      </p>
    );
  }

  const fieldH =
    'h-11 min-h-[44px] w-full min-w-0 rounded-xl border-2 px-3 text-base tabular-nums sm:h-10 sm:text-sm touch-manipulation';

  return (
    <div className="space-y-5 min-w-0 sm:space-y-6">
      <p
        className="text-sm leading-relaxed rounded-xl border px-4 py-3"
        style={{ borderColor: theme.border, backgroundColor: theme.panelInset, color: theme.textSecondary }}
      >
        Montants en <span className="font-semibold" style={{ color: theme.text }}>€ HT</span>. Une ou plusieurs
        lignes d’investissement par catégorie d’opération.
      </p>

      <div className="flex flex-col gap-4 md:flex-row md:items-stretch md:gap-5">
        <div
          className="min-w-0 flex-1 space-y-3 rounded-2xl border-2 px-4 py-4 shadow-sm"
          style={{ borderColor: theme.accent, backgroundColor: theme.accentSoft }}
        >
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p
              className="text-[11px] font-bold uppercase tracking-wide"
              style={{ color: theme.textSecondary }}
            >
              Total investissements client (€ HT)
            </p>
            <p className="text-lg font-bold tabular-nums shrink-0 sm:text-xl" style={{ color: theme.text }}>
              {fmtEur(totalClientHt)} HT
            </p>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p
              className="text-[11px] font-bold uppercase tracking-wide"
              style={{ color: theme.textSecondary }}
            >
              Total investissements prestataire (€ HT)
            </p>
            <p className="text-lg font-bold tabular-nums shrink-0 sm:text-xl" style={{ color: theme.text }}>
              {fmtEur(totalPrestataireHt)} HT
            </p>
          </div>
          {showAmortAnnuel && (
            <div
              className="flex flex-wrap items-center justify-between gap-2 border-t pt-3"
              style={{ borderColor: theme.border }}
            >
              <p
                className="min-w-0 max-w-[75%] text-[11px] font-bold uppercase tracking-wide leading-snug"
                style={{ color: theme.textSecondary }}
              >
                Amortissements annuels répercutés dans les frais fixes
              </p>
              <p className="text-lg font-bold tabular-nums shrink-0" style={{ color: theme.text }}>
                {fmtEur(amortAnnuelHt)} HT
              </p>
            </div>
          )}
        </div>

        <div
          className="flex w-full shrink-0 flex-col justify-center space-y-2 rounded-xl border-2 p-4 md:w-56"
          style={{ borderColor: theme.border, backgroundColor: theme.panelInset }}
        >
          <Label htmlFor="inv-annees" className="text-xs font-medium sm:text-sm" style={{ color: theme.textSecondary }}>
            Nombre d’années d’amortissement
          </Label>
          <Input
            id="inv-annees"
            type="number"
            min={0}
            step={1}
            inputMode="numeric"
            value={anneesStr}
            onChange={(e) => setAnnees(e.target.value)}
            disabled={!canEdit}
            className={fieldH}
            style={inputStyle}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-2 md:gap-5">
        {operationCategories.map((cat) => {
          const lines = linesByCategory[cat.id] || [];

          return (
            <Card
              key={cat.id}
              className="flex min-w-0 flex-col overflow-hidden rounded-2xl border shadow-sm"
              style={{ borderColor: theme.border, backgroundColor: theme.surface }}
            >
            <CardHeader
              className="border-b space-y-1 px-4 py-4"
              style={{ borderColor: theme.border, backgroundColor: theme.panelInset }}
            >
              <p className="text-sm font-semibold sm:text-base" style={{ color: theme.text }}>
                {cat.label || 'Catégorie'}
              </p>
              <p className="text-xs tabular-nums font-medium leading-relaxed sm:text-sm" style={{ color: theme.textSecondary }}>
                Total catégorie — client : {fmtEur(categoryInvestTotals[cat.id]?.clientHt ?? 0)} HT ·
                prestataire : {fmtEur(categoryInvestTotals[cat.id]?.prestataireHt ?? 0)} HT
              </p>
            </CardHeader>
            <CardContent className="flex flex-col gap-3 p-4 sm:p-5">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-xs font-bold uppercase tracking-wide sm:text-sm" style={{ color: theme.textSecondary }}>
                  Lignes
                </span>
                {canEdit && (
                  <Button
                    type="button"
                    variant="secondary"
                    className="h-10 min-h-[40px] gap-1.5 rounded-xl px-3 text-xs font-semibold sm:h-9 touch-manipulation"
                    onClick={() => addLine(cat.id)}
                  >
                    <Plus className="h-4 w-4 shrink-0" />
                    Ajouter une ligne
                  </Button>
                )}
              </div>
              <div className="space-y-3">
                {lines.length === 0 && (
                  <p className="text-xs py-1" style={{ color: theme.textSecondary }}>
                    Aucune ligne. Utilisez « Ajouter une ligne ».
                  </p>
                )}
                {lines.map((line) => (
                  <div
                    key={line.id}
                    className="grid grid-cols-1 gap-3 rounded-xl border-2 p-3 sm:grid-cols-12 sm:items-end sm:gap-3 sm:p-4"
                    style={{ borderColor: theme.border, backgroundColor: theme.panelInset }}
                  >
                    <div className="sm:col-span-3 space-y-1.5">
                      <Label className="text-xs font-medium sm:text-sm" style={{ color: theme.textSecondary }}>
                        Nom
                      </Label>
                      <Input
                        value={line.name}
                        onChange={(e) => updateLine(cat.id, line.id, { name: e.target.value })}
                        disabled={!canEdit}
                        placeholder="Nom"
                        className={`${fieldH} text-sm`}
                        style={inputStyle}
                      />
                    </div>
                    <div className="sm:col-span-4 space-y-1.5">
                      <Label className="text-xs font-medium sm:text-sm" style={{ color: theme.textSecondary }}>
                        Détails / descriptif
                      </Label>
                      <Textarea
                        value={line.details}
                        onChange={(e) => updateLine(cat.id, line.id, { details: e.target.value })}
                        disabled={!canEdit}
                        placeholder="Descriptif"
                        rows={2}
                        className="min-h-[88px] resize-y rounded-xl border-2 px-3 py-2.5 text-base sm:min-h-[72px] sm:text-sm touch-manipulation"
                        style={inputStyle}
                      />
                    </div>
                    <div className="sm:col-span-2 space-y-1.5">
                      <Label className="text-xs font-medium sm:text-sm" style={{ color: theme.textSecondary }}>
                        Client (€ HT)
                      </Label>
                      <Input
                        type="number"
                        min={0}
                        step={0.01}
                        inputMode="decimal"
                        value={line.client_ht === '' ? '' : line.client_ht}
                        onChange={(e) => updateLine(cat.id, line.id, { client_ht: e.target.value })}
                        disabled={!canEdit}
                        className={`${fieldH} text-right text-sm`}
                        style={inputStyle}
                      />
                    </div>
                    <div className="sm:col-span-2 space-y-1.5">
                      <Label className="text-xs font-medium sm:text-sm" style={{ color: theme.textSecondary }}>
                        Prestataire (€ HT)
                      </Label>
                      <Input
                        type="number"
                        min={0}
                        step={0.01}
                        inputMode="decimal"
                        value={line.prestataire_ht === '' ? '' : line.prestataire_ht}
                        onChange={(e) => updateLine(cat.id, line.id, { prestataire_ht: e.target.value })}
                        disabled={!canEdit}
                        className={`${fieldH} text-right text-sm`}
                        style={inputStyle}
                      />
                    </div>
                    {canEdit && (
                      <div className="sm:col-span-1 flex justify-end pb-0.5 sm:pb-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-11 w-11 min-h-[44px] min-w-[44px] text-destructive hover:text-destructive touch-manipulation"
                          aria-label="Supprimer la ligne"
                          onClick={() => removeLine(cat.id, line.id)}
                        >
                          <Trash2 className="h-5 w-5" />
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default DevisQuoteInvestissementTab;
